/*
 * FSK · Find Your Mushroom quiz.
 * Spec: content-drafts/website-preplan/copy-final/quiz.md
 *
 * Progressive enhancement: the section server-renders the full goal→product mapping. This file
 * hides that block and drives the interactive flow in its place. Anything that throws leaves the
 * static mapping on screen, so a failure degrades to a working page rather than a blank one.
 *
 * Vanilla, no framework, no dependency — the payload has to survive a farmers-market 4G signal.
 */
(function () {
  'use strict';

  var root = document.querySelector('.fsk-quiz');
  if (!root) return;

  var app = root.querySelector('[data-quiz-app]');
  var staticBlock = root.querySelector('[data-quiz-static]');
  var dataEl = document.querySelector('[data-quiz-data]');
  if (!app || !dataEl) return;

  var DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (e) {
    return; // static mapping stays visible
  }

  // ── Model ────────────────────────────────────────────────────────────────
  // Card labels are routing categories, not efficacy claims. No disease or symptom words.
  var GOALS = [
    { id: 'focus',    label: 'Focus',             gloss: 'Clear-headed, focused days.',                    icon: 'focus',    echo: 'focus',                  product: 'lm' },
    { id: 'rest',     label: 'Rest & calm',       gloss: 'Wind down in the evening.',                      icon: 'calm',     echo: 'rest',                   product: 'reishi' },
    { id: 'immune',   label: 'Immune & gut',      gloss: 'Everyday gut and immune support.',               icon: 'immune',   echo: 'immune and gut support', product: 'trifecta' },
    { id: 'recovery', label: 'Recovery',          gloss: 'Bounce back after training and physical strain.', icon: 'leaf',    echo: 'recovery',               product: 'trifecta' },
    { id: 'everyday', label: 'Everyday support',  gloss: 'A steady routine, morning and night.',           icon: 'calendar', echo: 'everyday support',       product: 'daynight' },
    { id: 'cooking',  label: 'Cooking (fresh mushrooms)', gloss: 'Fresh mushrooms to cook with. Market pickup only.', icon: 'saute', echo: 'fresh mushrooms to cook with', product: null }
  ];

  var TAKES = [
    { id: 'under_tongue', label: 'Under the tongue', gloss: 'Hold 1ml under your tongue for 30 to 60 seconds. Gets in the most directly.', icon: 'dropper' },
    { id: 'in_drink',     label: 'In a drink',       gloss: "Stir 1ml into cold water or your morning drink. Easy, and it tastes good.", icon: 'water' }
  ];

  var JOBS = [
    { id: 'replace',  label: 'Replace the meat',            serving: 'sear or dry-fry it like a steak' },
    { id: 'side',     label: 'A savory side',               serving: 'sauté it in butter as a side' },
    { id: 'addition', label: 'A meaty addition to a dish',  serving: 'sauté it into pastas, tacos, and stir-fries' }
  ];

  var FLAVORS = [
    { id: 'meaty', label: 'Mild, savory flavor with a seriously meaty texture', fresh: 'lm' },
    { id: 'rich',  label: 'Balanced: rich, meaty texture and flavor',           fresh: 'oyster' },
    { id: 'umami', label: 'Deep umami, classic mushroom character',             fresh: 'pioppino' }
  ];

  // Farmer's-note slots (Bank 11 EXP-QUIZ-1..5) are UNANSWERED and must never render placeholder
  // text to a shopper. Keyed by primary product; an empty string renders nothing at all.
  var NOTES = { reishi: '', lm: '', trifecta: '', daynight: '' };
  var NOTE_PRIORITY = ['rest', 'focus', 'immune', 'recovery', 'everyday'];

  var state = { goals: [], take: null, job: null, flavor: null, screen: 'q1' };

  // ── Resolver (§1, operates on the deduped PRODUCT set so equivalent sets always match) ──
  function resolveProducts(goalIds) {
    var set = [];
    goalIds.forEach(function (g) {
      var goal = byId(GOALS, g);
      if (goal && goal.product && set.indexOf(goal.product) === -1) set.push(goal.product);
    });

    // The old exact-match collapse ({LM, Reishi, Trifecta} -> Trifecta alone) is REMOVED.
    // RULED by Andrew 2026-07-20: "Focus should not collapse into Trifecta. Focus is purely
    // Lion's Mane." Trifecta is one blended bottle, so collapsing to it left a shopper who
    // asked for Focus with no dedicated Lion's Mane. This supersedes the 2026-07-13 ruling
    // that made Trifecta the one-bottle answer for {Focus, Rest, Immune}.
    //
    // Day & Night absorption below is deliberately KEPT: it is a two-bottle stack that
    // physically contains a Lion's Mane bottle, so Focus is still honored in full.

    // Absorption: a named stack replaces its components, then components are dropped.
    if (has(set, 'lm') && has(set, 'reishi')) {
      set = set.filter(function (p) { return p !== 'lm' && p !== 'reishi'; });
      if (!has(set, 'daynight')) set.push('daynight');
    }
    if (has(set, 'daynight')) {
      set = set.filter(function (p) { return p !== 'lm' && p !== 'reishi'; });
    }
    return set;
  }

  function primaryProduct(goalIds, products) {
    for (var i = 0; i < NOTE_PRIORITY.length; i++) {
      var goal = byId(GOALS, NOTE_PRIORITY[i]);
      if (goal && has(goalIds, goal.id) && has(products, goal.product)) return goal.product;
    }
    return products[0];
  }

  function benefitGoals() {
    return state.goals.filter(function (g) { return g !== 'cooking'; });
  }
  function path() {
    var b = benefitGoals().length, c = has(state.goals, 'cooking');
    if (b && c) return 'combined';
    if (c) return 'fresh';
    return 'extract';
  }

  // ── Copy helpers ─────────────────────────────────────────────────────────
  function joinList(items) {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    // "immune and gut support" already carries an "and", so an "and" join would stutter
    // ("focus and immune and gut support"). Comma-join the whole list in that case.
    if (items.some(function (i) { return i.indexOf(' and ') !== -1; })) return items.join(', ');
    if (items.length === 2) return items[0] + ' and ' + items[1];
    return items.slice(0, -1).join(', ') + ', and ' + items[items.length - 1];
  }
  function countWord(n) { return n === 1 ? 'one' : n === 2 ? 'pair' : 'set'; }

  function leadIn(products) {
    var echoes = benefitGoals().map(function (g) { return byId(GOALS, g).echo; });
    if (path() === 'fresh') {
      return "You're after fresh mushrooms to cook with, so here's the one I'd hand you at the market.";
    }
    if (path() === 'combined') {
      return "You're after " + joinList(echoes) + ", plus something fresh to cook, so here's what I'd start you on.";
    }
    return "You're after " + joinList(echoes) + ", so here's the " + countWord(products.length) + " I'd start you on.";
  }

  function microLabel(productKey) {
    var owning = benefitGoals().filter(function (g) {
      var goal = byId(GOALS, g);
      if (goal.product === productKey) return true;
      // Day & Night absorbs Focus and Rest.
      if (productKey === 'daynight' && (g === 'focus' || g === 'rest')) return true;
      return false;
    });
    if (!owning.length) return '';
    var labels = owning.map(function (g) { return byId(GOALS, g).label; });
    if (labels.length > 1) return 'Your ' + labels.join(' and ').replace(/Rest & calm/g, 'Rest') + ' pick';
    return 'Your ' + labels[0] + ' pick';
  }

  function usageNote(p) {
    if (!p.sublingual) return 'Usage: ' + p.usageDrink;
    return state.take === 'under_tongue'
      ? 'Usage: ' + p.usageTongue
      : 'Usage: ' + p.usageDrink;
  }

  // Genuine-OOS only: variant-level availability, and never for a product on the artifact
  // exclusion list (Lion's Mane storefront OOS is stale inventory data, not a real stockout).
  function isGenuinelyOOS(p) { return p.available === false && p.bisExempt !== true; }

  // ── Rendering ────────────────────────────────────────────────────────────
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function icon(name) {
    return '<svg aria-hidden="true"><use href="#fsk-ic-' + name + '"/></svg>';
  }
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function optionCard(opt, multi, selected) {
    var tag = multi ? 'label' : 'button';
    var n = el(tag, 'fsk-quiz__opt' + (selected ? ' is-selected' : ''));
    if (multi) {
      // The checkbox is stretched over the whole card in CSS (fsk-quiz__optinput) rather than
      // visually hidden, so every tap lands on the control itself. Relying on implicit label
      // activation left the card's padding dead to taps, which silently dropped a selection.
      n.innerHTML =
        '<input type="checkbox" class="fsk-quiz__optinput" value="' + opt.id + '"' + (selected ? ' checked' : '') + '>' +
        (opt.icon ? '<span class="fsk-quiz__opticon">' + icon(opt.icon) + '</span>' : '') +
        '<span class="fsk-quiz__optlabel">' + esc(opt.label) + '</span>' +
        (opt.gloss ? '<span class="fsk-quiz__optgloss">' + esc(opt.gloss) + '</span>' : '');
    } else {
      n.type = 'button';
      n.innerHTML =
        (opt.icon ? '<span class="fsk-quiz__opticon">' + icon(opt.icon) + '</span>' : '') +
        '<span class="fsk-quiz__optlabel">' + esc(opt.label) + '</span>' +
        (opt.gloss ? '<span class="fsk-quiz__optgloss">' + esc(opt.gloss) + '</span>' : '');
    }
    return n;
  }

  function screenShell(title, opts) {
    opts = opts || {};
    var wrap = el('div', 'fsk-glass fsk-quiz__panel');
    if (opts.back) {
      var back = el('button', 'fsk-quiz__back', '&larr; Back');
      back.type = 'button';
      back.addEventListener('click', opts.back);
      wrap.appendChild(back);
    }
    wrap.appendChild(el('h2', 'fsk-h fsk-h--md fsk-quiz__q', esc(title)));
    return wrap;
  }

  function render() {
    app.innerHTML = '';
    var fn = { q1: renderQ1, q2: renderQ2, q2f: renderQ2f, q3f: renderQ3f, result: renderResult }[state.screen];
    fn();
    app.hidden = false;
    if (staticBlock) staticBlock.hidden = true;
    syncUrl();
    var focusTarget = app.querySelector('h2');
    if (focusTarget) { focusTarget.setAttribute('tabindex', '-1'); focusTarget.focus({ preventScroll: true }); }
  }

  function renderQ1() {
    var panel = screenShell('What are you after?');
    var grid = el('div', 'fsk-quiz__grid');
    GOALS.forEach(function (g) {
      var card = optionCard(g, true, has(state.goals, g.id));
      card.querySelector('input').addEventListener('change', function () {
        if (this.checked) { if (!has(state.goals, g.id)) state.goals.push(g.id); }
        else state.goals = state.goals.filter(function (x) { return x !== g.id; });
        card.classList.toggle('is-selected', this.checked);
        cont.disabled = state.goals.length === 0;
        track('quiz_answer', { question: 'q1', value: state.goals.join(',') });
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    panel.appendChild(el('p', 'fsk-quiz__cue', "Pick as many as fit you and I'll build the set."));

    var cont = el('button', 'fsk-btn fsk-btn--primary fsk-quiz__continue', 'Show me what fits ' + icon('arrow'));
    cont.type = 'button';
    cont.disabled = state.goals.length === 0;
    cont.addEventListener('click', function () {
      if (!state.goals.length) return;
      state.screen = (path() === 'fresh') ? 'q2f' : 'q2';
      render();
    });
    panel.appendChild(cont);
    app.appendChild(panel);
  }

  function renderQ2() {
    var panel = screenShell('How do you want to take it?', { back: function () { state.screen = 'q1'; render(); } });
    var grid = el('div', 'fsk-quiz__grid fsk-quiz__grid--two');
    TAKES.forEach(function (t) {
      var card = optionCard(t, false, state.take === t.id);
      card.addEventListener('click', function () {
        state.take = t.id;
        track('quiz_answer', { question: 'q2', value: t.id });
        state.screen = 'result';
        render();
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    panel.appendChild(el('p', 'fsk-quiz__cue', 'Both work. Under the tongue gets in a little more directly; a drink is the easy way, and it tastes good.'));
    app.appendChild(panel);
  }

  function renderQ2f() {
    var panel = screenShell('What is the job in the kitchen?', { back: function () { state.screen = 'q1'; render(); } });
    var grid = el('div', 'fsk-quiz__grid fsk-quiz__grid--three');
    JOBS.forEach(function (j) {
      var card = optionCard(j, false, state.job === j.id);
      card.addEventListener('click', function () {
        state.job = j.id;
        track('quiz_answer', { question: 'q2f', value: j.id });
        state.screen = 'q3f';
        render();
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    app.appendChild(panel);
  }

  function renderQ3f() {
    var panel = screenShell('What flavor and texture are you after?', { back: function () { state.screen = 'q2f'; render(); } });
    var grid = el('div', 'fsk-quiz__grid fsk-quiz__grid--three');
    FLAVORS.forEach(function (f) {
      var card = optionCard(f, false, state.flavor === f.id);
      card.addEventListener('click', function () {
        state.flavor = f.id;
        track('quiz_answer', { question: 'q3f', value: f.id });
        state.screen = 'result';
        render();
      });
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    app.appendChild(panel);
  }

  // ── Result ───────────────────────────────────────────────────────────────
  function extractCard(key, showMicro) {
    var p = DATA.products[key];
    if (!p) return null;
    var card = el('article', 'fsk-quiz__card');
    var oos = isGenuinelyOOS(p);

    if (showMicro) {
      var ml = microLabel(key);
      if (ml) card.appendChild(el('p', 'fsk-quiz__micro-label', esc(ml)));
    }
    card.appendChild(el('h3', 'fsk-quiz__cardtitle',
      '<a class="fsk-link" href="' + esc(p.url) + refParam() + '">' + esc(p.title) + '</a>'));

    if (cameFrom(p.url)) {
      card.appendChild(el('p', 'fsk-quiz__affirm', "This is the one. Here's why it fits what you told me."));
    }
    card.appendChild(el('p', 'fsk-quiz__benefit', esc(p.benefit)));
    if (p.feel) card.appendChild(el('p', 'fsk-quiz__feel', esc(p.feel)));
    card.appendChild(el('p', 'fsk-quiz__usage', esc(usageNote(p))));

    // Reishi + Cooking: point at the fresh Oyster card already on this screen, never send away.
    if (key === 'reishi' && has(state.goals, 'cooking') && !has(state.goals, 'focus')) {
      card.appendChild(el('p', 'fsk-quiz__conditional',
        "Reishi is a medicinal mushroom, and we never sell it fresh. If you want something to cook with, the fresh Oyster below is the one I'd hand you."));
    }

    if (oos) {
      card.appendChild(formFor('bis', p.title));
    } else {
      var cta = el('a', 'fsk-btn fsk-btn--primary', 'Shop the ' + esc(p.title) + ' ' + icon('arrow'));
      cta.href = p.url + refParam();
      cta.addEventListener('click', function () { track('quiz_cta', { product: p.key }); });
      card.appendChild(cta);
    }

    card.appendChild(el('p', 'fsk-quiz__dshea', '<em>' + esc(DATA.dshea) + '</em>'));
    return card;
  }

  function freshCard(key) {
    var f = DATA.fresh[key];
    if (!f) return null;
    var card = el('article', 'fsk-quiz__card fsk-quiz__card--fresh');
    card.appendChild(el('h3', 'fsk-quiz__cardtitle',
      f.booth ? esc(f.title) : '<a class="fsk-link" href="' + esc(f.url) + refParam() + '">' + esc(f.title) + '</a>'));
    card.appendChild(el('p', 'fsk-quiz__benefit', esc(f.flavor)));
    card.appendChild(el('p', 'fsk-quiz__feel', esc(f.storage)));

    if (state.job) {
      var job = byId(JOBS, state.job);
      if (job) card.appendChild(el('p', 'fsk-quiz__usage', 'Serving: ' + esc(job.serving) + '.'));
    }

    if (f.booth) {
      var boothCta = el('a', 'fsk-btn fsk-btn--primary', esc(f.cta) + ' ' + icon('arrow'));
      boothCta.href = DATA.urls.markets + refParam();
      card.appendChild(boothCta);
    } else {
      var reserve = el('button', 'fsk-btn fsk-btn--primary', esc(f.cta));
      reserve.type = 'button';
      reserve.addEventListener('click', function () {
        if (card.querySelector('[data-market-form]')) return;
        var wrap = el('div', '');
        wrap.setAttribute('data-market-form', '');
        wrap.appendChild(formFor('market', f.title));
        card.appendChild(wrap);
        track('quiz_cta', { product: f.key, action: 'market_reminder' });
      });
      card.appendChild(reserve);
    }
    // Fresh is culinary only. No health claim, so no DSHEA on this card.
    return card;
  }

  function renderResult() {
    var panel = el('div', 'fsk-quiz__panel fsk-quiz__panel--result');
    var p = path();
    var products = p === 'fresh' ? [] : resolveProducts(state.goals);

    panel.appendChild(el('p', 'fsk-quiz__leadin', esc(leadIn(products))));

    if (products.length > 1) {
      panel.appendChild(el('h2', 'fsk-h fsk-h--md', 'Your stack'));
    } else {
      panel.appendChild(el('h2', 'fsk-visually-hidden', 'Your result'));
    }

    var cards = el('div', 'fsk-quiz__cards');
    products.forEach(function (key) {
      var c = extractCard(key, products.length > 1);
      if (c) cards.appendChild(c);
    });

    // Path B: the chosen fresh mushroom. Path C: one fresh secondary, no fresh sub-questions.
    if (p === 'fresh') {
      var chosen = state.flavor ? byId(FLAVORS, state.flavor).fresh : 'lm';
      var fc = freshCard(chosen);
      if (fc) cards.appendChild(fc);
    } else if (p === 'combined') {
      var secondary = has(state.goals, 'focus') ? 'lm' : 'oyster';
      var sc = freshCard(secondary);
      if (sc) { sc.classList.add('is-secondary'); cards.appendChild(sc); }
    }
    panel.appendChild(cards);

    // Farmer's note, one per screen, keyed to the primary rendered product. All slots are
    // currently unanswered, so this collapses to nothing.
    var note = NOTES[primaryProduct(state.goals, products)];
    if (note) panel.appendChild(el('blockquote', 'fsk-quiz__note', esc(note)));

    // Combined stack purchase, extracts only, never a fresh item, never a genuinely-OOS variant.
    if (products.length > 1) {
      var ids = products
        .map(function (k) { return DATA.products[k]; })
        .filter(function (x) { return x && x.variant && !isGenuinelyOOS(x); })
        .map(function (x) { return x.variant + ':1'; });
      if (ids.length > 1) {
        var stackCta = el('a', 'fsk-btn fsk-btn--secondary fsk-quiz__stackcta', 'Add your stack ' + icon('arrow'));
        stackCta.href = '/cart/' + ids.join(',');
        stackCta.addEventListener('click', function () { track('quiz_cta', { action: 'add_stack' }); });
        panel.appendChild(stackCta);
      }
    }

    if (p === 'fresh' || p === 'combined') {
      panel.appendChild(el('p', 'fsk-quiz__bridge',
        'Fresh is market pickup at our Central Florida markets. Not nearby? Our extracts are available online, anywhere &rarr; ' +
        '<a class="fsk-link" href="' + DATA.urls.extracts + '">see all the extracts</a>.'));
    }

    // Email capture renders only after a result. Fresh-only results get the recipe capture;
    // the extract "your result, saved" block (which promises the usage guide and mocktail
    // e-book) belongs to extract and combined screens, not to a purely culinary one.
    if (p === 'fresh') {
      var chosenFresh = state.flavor ? byId(FLAVORS, state.flavor).fresh : 'lm';
      panel.appendChild(formFor('recipes', DATA.fresh[chosenFresh].title));
    } else {
      panel.appendChild(formFor('result', products.map(function (k) { return DATA.products[k].title; }).join(', ')));
    }

    if (p === 'fresh') {
      panel.appendChild(el('p', 'fsk-quiz__links',
        '<a class="fsk-link" href="' + DATA.urls.markets + '">Come see us at the booth</a> &middot; ' +
        '<a class="fsk-link" href="' + DATA.urls.recipes + '">Recipes</a>'));
    } else if (p === 'combined') {
      panel.appendChild(el('p', 'fsk-quiz__links',
        '<a class="fsk-link" href="' + DATA.urls.fresh + '">Browse the fresh mushrooms</a> &middot; ' +
        '<a class="fsk-link" href="' + DATA.urls.markets + '">Come see us at the booth</a> &middot; ' +
        '<a class="fsk-link" href="' + DATA.urls.recipes + '">Recipes</a>'));
    }

    // Persistent escape + start over. The "compare the mushrooms" link is dropped for launch:
    // no comparison page exists (ruled 2026-07-19).
    var esc_ = el('p', 'fsk-quiz__escape',
      'Not quite? <a class="fsk-link" href="' + DATA.urls.extracts + '">See all the extracts</a>.');
    panel.appendChild(esc_);

    var over = el('button', 'fsk-quiz__startover', 'Start over');
    over.type = 'button';
    over.addEventListener('click', function () {
      state = { goals: [], take: null, job: null, flavor: null, screen: 'q1' };
      render();
    });
    panel.appendChild(over);

    app.appendChild(panel);
    track('quiz_complete', { path: p, goals: state.goals.join(','), result: products.join(',') });
  }

  // ── Forms ────────────────────────────────────────────────────────────────
  function formFor(kind, resultLabel) {
    var tpl = document.querySelector('[data-quiz-form="' + kind + '"]');
    var wrap = el('div', 'fsk-quiz__formwrap');
    if (!tpl) return wrap;
    wrap.appendChild(tpl.content.cloneNode(true));
    setField(wrap, 'result', resultLabel || '');
    setField(wrap, 'goals', state.goals.join(','));
    setField(wrap, 'path', path());
    setField(wrap, 'take', state.take || '');
    return wrap;
  }
  function setField(scope, name, value) {
    var f = scope.querySelector('[data-quiz-field="' + name + '"]');
    if (f) f.value = value;
  }

  // ── URL state ────────────────────────────────────────────────────────────
  function syncUrl() {
    if (!window.history || !window.history.replaceState) return;
    var params = new URLSearchParams();
    if (state.goals.length) params.set('goals', state.goals.join(','));
    if (state.take) params.set('take', state.take);
    if (state.job) params.set('job', state.job);
    if (state.flavor) params.set('flavor', state.flavor);
    var ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) params.set('ref', ref);
    var q = params.toString();
    window.history.replaceState(null, '', q ? '?' + q : window.location.pathname);
  }

  // Unknown or conflicting params fall back to the intro rather than erroring.
  function readUrl() {
    var params = new URLSearchParams(window.location.search);
    var goals = (params.get('goals') || '').split(',').filter(function (g) {
      return g && GOALS.some(function (x) { return x.id === g; });
    });
    if (!goals.length) return false;
    state.goals = goals;
    var take = params.get('take');
    state.take = TAKES.some(function (t) { return t.id === take; }) ? take : null;
    var job = params.get('job');
    state.job = JOBS.some(function (j) { return j.id === job; }) ? job : null;
    var flavor = params.get('flavor');
    state.flavor = FLAVORS.some(function (f) { return f.id === flavor; }) ? flavor : null;

    if (path() === 'fresh' && !state.flavor) { state.screen = 'q2f'; return true; }
    if (path() !== 'fresh' && !state.take) { state.screen = 'q2'; return true; }
    state.screen = 'result';
    return true;
  }

  function cameFrom(productUrl) {
    var ref = document.referrer || '';
    return ref.indexOf(productUrl) !== -1;
  }
  function refParam() {
    var g = benefitGoals()[0] || (has(state.goals, 'cooking') ? 'fresh' : 'quiz');
    return '?ref=quiz-' + g;
  }

  // ── Misc ─────────────────────────────────────────────────────────────────
  function byId(list, id) { return list.filter(function (x) { return x.id === id; })[0]; }
  function has(arr, v) { return arr.indexOf(v) !== -1; }
  function track(name, detail) {
    document.dispatchEvent(new CustomEvent('fsk:' + name, { detail: detail }));
    if (window.dataLayer && window.dataLayer.push) {
      window.dataLayer.push(Object.assign({ event: 'fsk_' + name }, detail || {}));
    }
  }

  // ── Boot ─────────────────────────────────────────────────────────────────
  try {
    var deepLinked = readUrl();
    render();
    track('quiz_start', { deep_linked: deepLinked });
  } catch (e) {
    // Leave the server-rendered mapping in place.
    app.hidden = true;
    if (staticBlock) staticBlock.hidden = false;
  }
})();
